import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtDate } from "../lib/api";
import { PageHeader, DataTable, DateRangeFilter, filterByDate } from "../components/Shell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DatePicker } from "../components/DatePicker";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Timesheets() {
  const [list, setList] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ start: "", end: "" });
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilter = searchParams.get("filter") || "";
  const [f, setF] = useState({
    personnel_id: "",
    date_jour: new Date().toISOString().slice(0, 10),
    heure_arrivee: "",
    heure_depart: "",
    pause_minutes: 0,
    commentaire: "",
  });

  const load = async () => {
    const [t, s] = await Promise.all([api.get("/timesheets"), api.get("/staff")]);
    setList(t.data);
    setStaff(s.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const staffById = useMemo(() => Object.fromEntries(staff.map((s) => [s.id, s])), [staff]);

  const tsColumns = useMemo(() => [
    { title: "Date", render: (t) => fmtDate(t.date_jour) },
    { title: "Personnel", render: (t) => {
        const s = staffById[t.personnel_id];
        return s ? `${s.prenom} ${s.nom}` : t.personnel_id;
      } },
    { title: "Type", render: (t) => staffById[t.personnel_id]?.type_personnel || "-" },
    { title: "Arrivée", render: (t) => t.heure_arrivee || "-" },
    { title: "Départ", render: (t) => t.heure_depart || "-" },
    { title: "Pause", render: (t) => (t.pause_minutes ? `${t.pause_minutes} min` : "-") },
    { title: "Heures", align: "right", render: (t) => <b style={{ color: t.incomplet ? "#f87171" : "#34d399" }}>{(t.duree_heures || 0).toFixed(2)} h</b> },
    {
      title: "",
      render: (t) =>
        t.incomplet ? <Warning size={16} color="#f87171" title="Pointage incomplet" /> : null,
    },
  ], [staffById]);

  const submit = async () => {
    if (!f.personnel_id) { toast.error("Sélectionnez un membre du personnel"); return; }
    try {
      await api.post("/timesheets", f);
      toast.success("Pointage enregistré");
      setOpen(false);
      setF({ ...f, heure_arrivee: "", heure_depart: "", commentaire: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div>
      <PageHeader
        testId="timesheets-header"
        title="Pointages"
        subtitle="Heures d'arrivée/départ pour personnel et agents de sécurité."
        actions={
          <div className="flex gap-3 items-center flex-wrap">
            <DateRangeFilter testId="ts-filter" start={range.start} end={range.end} onChange={setRange} />
            <Button data-testid="new-timesheet" onClick={() => setOpen(true)} className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black">
              <Plus size={16} className="mr-2" /> Nouveau pointage
            </Button>
          </div>
        }
      />
      {urlFilter === "incomplets" && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-md border" style={{ borderColor: "#f8717150", background: "#f8717110" }}>
          <span className="text-xs text-[#f87171]">Filtre actif : <b>Pointages incomplets</b></span>
          <button
            data-testid="clear-url-filter-ts"
            onClick={() => setSearchParams({})}
            className="text-xs text-[#a1a1aa] hover:text-white underline ml-2"
          >
            effacer
          </button>
        </div>
      )}

      <DataTable
        columns={tsColumns}
        rows={(() => {
          let rows = (range.start || range.end) ? filterByDate(list, "date_jour", range.start, range.end) : list;
          if (urlFilter === "incomplets") rows = rows.filter((t) => t.incomplet);
          return rows;
        })()}
        rowKey="id"
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121217] border-[#27272a] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Nouveau pointage</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <Label className="label-xs mb-2 block">1. Personnel</Label>
              <Select value={f.personnel_id} onValueChange={(v) => setF({ ...f, personnel_id: v })}>
                <SelectTrigger data-testid="ts-staff" className="bg-[#09090b] border-[#27272a] h-12 text-base">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent className="bg-[#121217] border-[#27272a] text-white">
                  {staff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom} ({s.type_personnel})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="label-xs mb-2 block">Date</Label>
              <DatePicker testId="ts-date" value={f.date_jour} onChange={(v) => setF({ ...f, date_jour: v })} className="h-12" />
            </div>

            <div>
              <Label className="label-xs mb-2 block">2. Heure d'arrivée</Label>
              <div className="grid grid-cols-5 gap-2">
                {["22:00", "22:15", "22:30", "22:45", "23:00"].map((t) => {
                  const active = f.heure_arrivee === t;
                  const isDefault = t === "22:30";
                  return (
                    <button
                      key={t}
                      type="button"
                      data-testid={`arr-${t}`}
                      onClick={() => setF({ ...f, heure_arrivee: t })}
                      className="h-14 rounded-md border font-mono text-lg font-semibold transition-all duration-150 hover:scale-[1.03]"
                      style={{
                        color: active ? "#09090b" : isDefault ? "#fbbf24" : "#ffffff",
                        background: active ? "#fbbf24" : isDefault ? "#fbbf2414" : "#09090b",
                        borderColor: active ? "transparent" : isDefault ? "#fbbf24" : "#27272a",
                      }}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              <Input
                data-testid="ts-arr-other"
                type="time"
                value={f.heure_arrivee}
                onChange={(e) => setF({ ...f, heure_arrivee: e.target.value })}
                className="bg-[#09090b] border-[#27272a] h-10 mt-2 max-w-[160px]"
              />
            </div>

            <div>
              <Label className="label-xs mb-2 block">3. Heure de départ</Label>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
                {["03:00", "03:30", "04:00", "04:30", "05:00", "05:30", "06:00"].map((t) => {
                  const active = f.heure_depart === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      data-testid={`dep-${t}`}
                      onClick={() => setF({ ...f, heure_depart: t })}
                      className="h-14 rounded-md border font-mono text-lg font-semibold transition-all duration-150 hover:scale-[1.03]"
                      style={{
                        color: active ? "#09090b" : "#ffffff",
                        background: active ? "#fbbf24" : "#09090b",
                        borderColor: active ? "transparent" : "#27272a",
                      }}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              <Input
                data-testid="ts-dep-other"
                type="time"
                value={f.heure_depart}
                onChange={(e) => setF({ ...f, heure_depart: e.target.value })}
                className="bg-[#09090b] border-[#27272a] h-10 mt-2 max-w-[160px]"
              />
            </div>

            <details>
              <summary className="label-xs cursor-pointer hover:text-white select-none py-1">
                Pause & commentaire (optionnel)
              </summary>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                <div>
                  <Label className="label-xs mb-1 block">Pause (minutes)</Label>
                  <Input type="number" min="0" value={f.pause_minutes} onChange={(e) => setF({ ...f, pause_minutes: Number(e.target.value) })} className="bg-[#09090b] border-[#27272a] h-10" />
                </div>
                <div>
                  <Label className="label-xs mb-1 block">Commentaire</Label>
                  <Input value={f.commentaire} onChange={(e) => setF({ ...f, commentaire: e.target.value })} className="bg-[#09090b] border-[#27272a] h-10" />
                </div>
              </div>
            </details>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#27272a] hover:bg-[#1a1a21] text-white h-12">Annuler</Button>
            <Button data-testid="submit-ts" onClick={submit} className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black h-12 px-8 text-base font-semibold">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
